import { useState } from 'react';

import { Stack } from '@mui/material';

import type {
  ConversationalFormOption,
  ConversationalFormStep,
} from '@karehero/models';

import { ChatActionRow } from '@/pwa/molecules/ChatActionRow/ChatActionRow';
import { answersArrivalSx } from './arrival';

const MULTI_SELECT_DONE_LABEL = "Continue when you're done";

/** Rows of their own under the question, or buttons joined to it. */
export type ActionRowsLayout = 'rows' | 'attached';

export interface ActionRowsProps {
  step: ConversationalFormStep;
  isSubmitting?: boolean;
  onChoose: (option: ConversationalFormOption) => void;
  /** Multi-select steps answer with every option picked. */
  onChooseMany?: (options: ConversationalFormOption[]) => void;
  layout?: ActionRowsLayout;
}

/** Picks an option, keeping exclusive ones on their own. */
const toggle = (
  picked: ConversationalFormOption[],
  option: ConversationalFormOption,
) => {
  if (picked.some((o) => o.id === option.id)) {
    return picked.filter((o) => o.id !== option.id);
  }
  if (option.isExclusive) return [option];
  return [...picked.filter((o) => !o.isExclusive), option];
};

/** ActionRows renders a step's options under the thread, whatever its mode. */
export const ActionRows = ({
  step,
  isSubmitting,
  onChoose,
  onChooseMany,
  layout = 'rows',
}: ActionRowsProps) => {
  // Keyed by step where it is rendered, so picks never carry to the next one.
  const [picked, setPicked] = useState<ConversationalFormOption[]>([]);
  const answers = step.options ?? [];
  if (answers.length === 0) return null;

  if (step.isMultiSelect) {
    return (
      <Stack spacing={2} sx={[{ px: 3, pb: 2 }, answersArrivalSx]}>
        {answers.map((option) => (
          <ChatActionRow
            key={option.id}
            label={option.label}
            icon={
              picked.some((o) => o.id === option.id) ? 'check-square' : 'square'
            }
            isDisabled={isSubmitting}
            onClick={() => setPicked((current) => toggle(current, option))}
          />
        ))}
        <ChatActionRow
          label={MULTI_SELECT_DONE_LABEL}
          icon='chevron-right'
          tone='go'
          isDisabled={isSubmitting || picked.length === 0}
          onClick={() => onChooseMany?.(picked)}
        />
      </Stack>
    );
  }

  if (layout === 'attached') {
    return (
      <Stack
        spacing={0.5}
        role='group'
        aria-label={step.ask}
        sx={[{ px: 3, pb: 2, width: '86%' }, answersArrivalSx]}
      >
        {answers.map((option) => (
          <ChatActionRow
            key={option.id}
            label={option.label}
            tone='attached'
            isQuiet={option.isQuiet}
            isDisabled={isSubmitting}
            onClick={() => onChoose(option)}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={[{ px: 3, pb: 2 }, answersArrivalSx]}>
      {answers.map((option) => (
        <ChatActionRow
          key={option.id}
          label={option.label}
          icon={option.icon ?? (option.isPrimary ? 'chevron-right' : undefined)}
          tone={option.isPrimary ? 'go' : undefined}
          isDisabled={isSubmitting}
          onClick={() => onChoose(option)}
        />
      ))}
    </Stack>
  );
};

ActionRows.displayName = 'ActionRows';
export default ActionRows;
